import { ChangeDetectionStrategy, Component, computed, EventEmitter, input, Input, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { isEqual } from 'lodash-es';

import { AUTOCOMPLETE_DEBOUNCE_TIME } from '../../../core/autocomplete/constants/autocomplete.constants';
import { ShFormGroupComponent } from '../../../form-controls/components/_core/form-group/form-group.component';
import { ShIndeterminateDirective } from '../../../form-controls/directives/indeterminate.directive';
import { FormGroupStyling } from '../../../form-controls/entities/form-group-styling.entity';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { FormValueState } from '../../../form-controls/entities/form-value.entity';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { ShChildIndexDirective } from '../../directives/child-index/child-index';
import { ShClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';
import { ShActionItemComponent } from '../action-item/action-item.component';
import { ActionItem, ActionItemStyle } from '../action-item/entities/action-item.entity';
import { BaseDroppanelSelectComponent } from '../base-droppanel-select/base-droppanel-select.abstract';
import { ShDroppanelComponent } from '../droppanel/droppanel.component';
import { ShIconModule } from '../icon/icon.module';

@Component({
    standalone: true,
    selector: 'sh-live-options-select',
    templateUrl: './live-options-select.component.html',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ShIconModule,
        ShDroppanelComponent,
        ShIndeterminateDirective,
        ShFormGroupComponent,
        ShActionItemComponent,
        ShChildIndexDirective,
        ShClickOutsideDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShLiveOptionsSelectComponent<T = string> extends BaseDroppanelSelectComponent<T> {
    /* Override to remove @Input decorator behavior - this component manages its own state */
    public override isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /* Options */
    public options$: BehaviorSubject<FormSelectOption<T>[]> = new BehaviorSubject<FormSelectOption<T>[]>([]);
    public currentPage$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    /* Label */
    public selectLabel$: Observable<string>;

    /* Action */
    noResultsActionMsg = input(null);
    alwaysShowNoResultsAction = input(false);
    $noResultsAction = computed(() => this.getNoResultActionItem(this.noResultsActionMsg()));
    showNoResultsAction$: Observable<boolean>;

    /* Messages */
    @Input() label: string;
    @Input() placeholderMsg: string;
    @Input() showMoreMsg = this.translateService.translate('ui.load-more');
    @Input() selectionLimitMessageKey: string = 'ui.selection-limit';

    @Input() formGroupStyling: FormGroupStyling[] = [];

    /* Specific inputs */
    @Input() resultsPerPage = 50;

    /* Paginated entity functions */
    @Input() paginatedResultsFct!: (term: string, pagination: PaginationInfo) => Observable<PaginatedResults<T>>;

    @Output() selectionState: EventEmitter<T[]> = new EventEmitter<T[]>();
    @Output() noResultsActionTrigger: EventEmitter<string> = new EventEmitter<string>();

    constructor(formBuilder: UntypedFormBuilder) {
        super(formBuilder);
    }

    protected override onInit(): void {
        this.initOptionsStream();
        this.initMessageStream();
        this.initNoResultsActionStream();
    }

    private getNoResultActionItem(noResultsActionMsg: string | null): ActionItem | null {
        if (noResultsActionMsg) {
            return {
                label: noResultsActionMsg,
                style: ActionItemStyle.Link,
                eventListener: this.onNoResultsAction.bind(this)
            };
        }
        return null;
    }

    private onNoResultsAction(): void {
        this.noResultsActionTrigger.emit(this.searchControl.value);
    }

    private initNoResultsActionStream(): void {
        this.showNoResultsAction$ = this.options$.pipe(map(options => this.calculateShowNoResultsAction(options)));
    }

    private calculateShowNoResultsAction(options: FormSelectOption<T>[]): boolean {
        if (!this.noResultsActionMsg()) {
            return false;
        }

        const searchTermEntered: boolean = this.searchControl.value.trim().length > 0;

        if (!searchTermEntered) {
            return false;
        }

        if (this.alwaysShowNoResultsAction() && searchTermEntered) {
            return true;
        }

        const hasMatchingOptions: boolean = options.some(option =>
            option.label.toLowerCase().startsWith(this.searchControl.value.toLowerCase())
        );
        return !hasMatchingOptions;
    }

    private initOptionsStream(): void {
        const latestPage$: Observable<PaginatedResults<T>> = combineLatest([
            this.currentPage$,
            this.searchTerm$,
            this.isOpen$
        ]).pipe(
            filter(([page, searchTerm, isOpen]) => isOpen),
            debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME),
            distinctUntilChanged(isEqual),
            tap(([page, searchTerm]) => {
                if (page === 0) {
                    this.loading$.next(true);
                }
            }),
            switchMap(([page, searchTerm]) =>
                this.paginatedResultsFct(searchTerm, {
                    page,
                    resultsPerPage: this.resultsPerPage
                })
            ),
            shareReplay()
        );

        this.showExtraItemsLabelIndicator$ = latestPage$.pipe(
            map(
                paginatedResults =>
                    paginatedResults.paginationInfo.totalNumberOfResults >
                    (paginatedResults.paginationInfo.page + 1) * paginatedResults.paginationInfo.resultsPerPage
            )
        );

        latestPage$.pipe(takeUntil(this.destroy$)).subscribe(latestPage => {
            const formSelectOptions: FormSelectOption<T>[] = latestPage.results.map(value => ({
                label: this.valueToLabelFct!(value),
                value
            }));

            if (latestPage.paginationInfo.page === 0) {
                this.options$.next(formSelectOptions);
            } else {
                const updatedOptions: FormSelectOption<T>[] = [...this.options$.value, ...formSelectOptions];
                this.options$.next(updatedOptions);
            }

            this.loading$.next(false);
        });
    }

    private initMessageStream(): void {
        this.selectLabel$ = this.selectedValues$.pipe(
            map(selected => {
                if (selected.length === 0) {
                    return this.placeholderMsg;
                }
                if (selected.length === 1) {
                    return this.valueToLabelFct!(selected[0]);
                }
                return this.translateService.translate('general.filter.multipleSelected');
            })
        );
    }

    protected override initListeners(): void {
        super.initListeners();
        this.initSearchListener();
        this.initSearchFocusListener();
    }

    private initSearchListener(): void {
        this.searchTerm$.pipe(takeUntil(this.destroy$)).subscribe(_ => this.resetPage());
    }

    private initSearchFocusListener(): void {
        this.isOpen$
            .pipe(
                filter(() => !!this.search),
                filter(isOpen => !!isOpen),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.focusOnSearch();
            });
    }

    protected override initStateChangeListener(): void {
        this.state$
            .pipe(
                takeUntil(this.destroy$),
                map(state => state.filter(objectState => objectState.state === FormValueState.SET)),
                map(state => state.map(selected => selected.value.value))
            )
            .subscribe(state => this.selectionState.emit(state));
    }

    protected override initHasOptionsStream(): void {
        this.hasOptions$ = this.options$.pipe(
            take(1),
            map(() => true)
        );
    }

    // #region Search
    private focusOnSearch(): void {
        setTimeout(() => this.searchElement?.nativeElement.focus(), 0);
    }

    private resetPage(): void {
        this.currentPage$.next(0);
    }
    // #endregion

    // #region Load more
    protected override getLoadMoreLabel(): string {
        return this.showMoreMsg;
    }

    protected override onLoadMore(): void {
        this.currentPage$.next(this.currentPage$.value + 1);
    }
    // #endregion

    // #region Helpers
    public toggleDropdown(): void {
        this.isOpen$.next(!this.isOpen$.value);
    }
    // #endregion
}
